import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BeatLoader } from 'react-spinners';
import '../../../../../styles/count_graphs.css';
import { url } from '../../../../../api/endpointURL';
import { getYearlyTotal,
    getPercentageIncrease,
    getTotalForPeriod,
    filterMonthlyDataByCamera,
    filterWeeklyDataByCamera,
    filterDailyDataByCamera,
    checkForEmptyData
} from '../baseLprUtils';
import MonthlyOccupancyChart from '../../baseSuite/PeopleOccupancy/MonthlyOccupancyChart';
import WeeklyOccupancyChart from '../../baseSuite/PeopleOccupancy/WeeklyOccupancyChart';
import DailyOccupancyChart from '../../baseSuite/PeopleOccupancy/DailyOccupancyChart';

const LprOccupancyAnalytics = ({
    selectedSite,
    selectedCameraId,
    selectedYear,
    handleOccupancyDataState
}) => {
    const [monthlyOccupancyData, setMonthlyOccupancyData] = useState([]);
    const [monthlyLoading, setMonthlyLoading] = useState(false);

    const [weeklyOccupancyData, setWeeklyOccupancyData] = useState([]);
    const [weeklyLoading, setWeeklyLoading] = useState(false);
    
    const [dailyOccupancyData, setDailyOccupancyData] = useState([]);
    const [dailyLoading, setDailyLoading] = useState(false);

    const [selectedMonth, setSelectedMonth] = useState();
    const [selectedWeek, setSelectedWeek] = useState();
    const [selectedWeekStartDate, setSelectedWeekStarDate] = useState();
    const [selectedRelativeWeek, setSelectedRelativeWeek] = useState();
    const [selectedDate, setSelectedDate] = useState();
    const [selectedDayOfWeek, setSelectedDayOfWeek] = useState();

    const [totalAlerts, setTotalAlerts] = useState();

    const monthlyAbortControllerRef = useRef(null);
    const weeklyAbortControllerRef = useRef(null);
    const dailyAbortControllerRef = useRef(null);

    const analyticType = 'lprOccupancy';

    const aggregatedMonthlyData = useMemo(() => 
        filterMonthlyDataByCamera(monthlyOccupancyData, selectedCameraId, 'total_count'),
        [monthlyOccupancyData, selectedCameraId]
    );

    const aggregatedWeeklyData = useMemo(() => 
        filterWeeklyDataByCamera(weeklyOccupancyData, selectedCameraId, 'total_count'),
        [weeklyOccupancyData, selectedCameraId]
    );

    const aggregatedDailyData = useMemo(() => 
        filterDailyDataByCamera(dailyOccupancyData, selectedCameraId, 'total_count'),
        [dailyOccupancyData, selectedCameraId]
    );

    const [initialFetchCompleted, setInitialFetchCompleted] = useState(false);
    const [showOccupancy, setShowOccupancy] = useState(true);

    const prevCameraRef = useRef(selectedCameraId);

    useEffect(() => {
        const cameraChanged = prevCameraRef.current !== selectedCameraId;
        prevCameraRef.current = selectedCameraId;

        if (cameraChanged) {
            setInitialFetchCompleted(false);
        }
    }, [selectedCameraId]);

    const hasActualData = useCallback(() => {
        const hasData = checkForEmptyData(aggregatedMonthlyData);
        if (!hasData) {
            return false;
        } else {
            return true;
        }
    }, [aggregatedMonthlyData]);

    const fetchMonthlyOccupancyData = useCallback(async () => {
        if (monthlyAbortControllerRef.current) {
            monthlyAbortControllerRef.current.abort();
        }

        monthlyAbortControllerRef.current = new AbortController();
        const { signal } = monthlyAbortControllerRef.current;
        try {
            // console.log("fetching monthly lpr occupancy data");

            // console.log("hwid in the fetch monthly lpr occupancy function ::::", selectedSite.hwid);

            setMonthlyLoading(true);

            const response = await fetch(`${url}/occupancy/monthlyOccupancy?year=${selectedYear}&hwid=${selectedSite?.hwid}&analyticType=${analyticType}`, { signal });
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }
            const responseData = await response.json();

            // console.log("monthly lpr occupancy data ========= ", responseData);

            setMonthlyLoading(false);

            setMonthlyOccupancyData(responseData);

            setInitialFetchCompleted(true);

            if (responseData.length) {
                // console.log("found response data from monthly lpr occupancy fetch. Data === ", responseData);
                const latestMonthWithData = [...responseData]
                    .reverse()
                    .find(month => month.total_count > 0);

                if (latestMonthWithData) {
                    // console.log("found latest month with data.");
                    // console.log("setting the selected month to: ", latestMonthWithData.month);
                    setSelectedMonth(latestMonthWithData.month);
                }
            }

            // console.log("selected month after being set post monthly fetch is: ", selectedMonth);


        } catch (e) {
            if (e.name === 'AbortError') {
                // console.log("Fetch monthly lpr occupancy data aborted");
            }
            // console.log(e)
        } finally {
            setMonthlyLoading(false);
        }
    }, [selectedYear, selectedSite.hwid]);

    const fetchWeeklyOccupancyData = useCallback(async () => {
        if (weeklyAbortControllerRef.current) {
            weeklyAbortControllerRef.current.abort();
        }

        weeklyAbortControllerRef.current = new AbortController();
        const { signal } = weeklyAbortControllerRef.current;
        try {
            // console.log("fetching weekly lpr occupancy data");

            setWeeklyLoading(true);

            const response = await fetch(`${url}/occupancy/weeklyOccupancy?year=${selectedYear}&month=${selectedMonth}&hwid=${selectedSite?.hwid}&analyticType=${analyticType}`, { signal });
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }
            const responseData = await response.json();

            // console.log("weekly lpr occupancy data ========= ", responseData);

            setWeeklyLoading(false);

            setWeeklyOccupancyData(responseData);

            if (responseData.length) {
                const latestWeekWithData = [...responseData]
                    .reverse()
                    .find(week => week.total_count > 0);

                if (latestWeekWithData) {
                    // console.log("found latest week with data.");
                    // console.log("setting the selected week to: ", latestWeekWithData.week);
                    setSelectedWeek(latestWeekWithData.week);
                    setSelectedWeekStarDate(latestWeekWithData.week_start_date);
                    setSelectedRelativeWeek(latestWeekWithData.relative_week_number);
                }
            }

        } catch (e) {
            if (e.name === 'AbortError') {
                // console.log("fetching weekly lpr occupancy data aborted");
            }
            // console.log(e)
        } finally {
            setWeeklyLoading(false);
        }
    }, [selectedYear, selectedMonth, selectedSite.hwid]);

    const fetchDailyOccupancyData = useCallback(async () => {
        if (dailyAbortControllerRef.current) {
            dailyAbortControllerRef.current.abort();
        }

        dailyAbortControllerRef.current = new AbortController();
        const { signal } = dailyAbortControllerRef.current;
        try {
            // console.log("fetching daily lpr occupancy data");
            // console.log("The selected week prior to fetching lpr occupancy data::: ", selectedWeek);

            setDailyLoading(true);

            const response = await fetch(`${url}/occupancy/dailyOccupancy?year=${selectedYear}&month=${selectedMonth}&week=${selectedWeek}&hwid=${selectedSite?.hwid}&analyticType=${analyticType}`, {signal});
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }
            const responseData = await response.json();

            // console.log("daily lpr occupancy data ========= ", responseData);

            setDailyLoading(false);

            setDailyOccupancyData(responseData);

            if (responseData.length) {
                const latestDayWithData = [...responseData]
                    .reverse()
                    .find(day => day.total_count > 0);

                if (latestDayWithData) {
                    // console.log("found latest day with data.");
                    // console.log("setting the selected day to: ", latestDayWithData.day_number);
                    setSelectedDate(latestDayWithData.day_number);
                    setSelectedDayOfWeek(latestDayWithData.day);
                }
            }

        } catch (e) {
            if (e.name === "AbortError") {
                // console.log("daily lpr occupancy data fetch aborted.");
            } 
            // console.log(e)
        } finally {
            setDailyLoading(false);
        }
    }, [selectedYear, selectedMonth, selectedWeek, selectedSite?.hwid]);
    
    useEffect(() => {
        return () => {
            if (monthlyAbortControllerRef.current) monthlyAbortControllerRef.current.abort();
            if (weeklyAbortControllerRef.current) weeklyAbortControllerRef.current.abort();
            if (dailyAbortControllerRef.current) dailyAbortControllerRef.current.abort();
        };
    }, []);

    useEffect(() => {
        if (initialFetchCompleted) {
            const hasData = hasActualData();
            setShowOccupancy(hasData);

            if (handleOccupancyDataState) {
                handleOccupancyDataState(hasData);
            }
        }
    }, [initialFetchCompleted, hasActualData, handleOccupancyDataState]);

    useEffect(() => {
        if (monthlyOccupancyData.length > 0) {
            let total = getYearlyTotal(monthlyOccupancyData, 'total_count');
            // console.log("yearly total occupancy in the useEffect is: ", total);
            setTotalAlerts(total);
        }
    }, [monthlyOccupancyData])

    useEffect(() => {
        // console.log("hwid in the useEffect prior to fetching the monthly lpr occupancy data", selectedSite?.hwid);
        if (selectedYear && selectedSite?.hwid) {
            fetchMonthlyOccupancyData();
        }
    }, [selectedYear, selectedSite?.hwid, fetchMonthlyOccupancyData]);

    useEffect(() => {
        if (aggregatedMonthlyData.length) {
            // console.log("monthly data for this year exists. Fetching Weekly data.");
            // console.log("the selected month in the useEffect prior to fetching weekly data is: ", selectedMonth);
            fetchWeeklyOccupancyData();
        }

    }, [selectedYear, selectedMonth, selectedSite?.hwid, aggregatedMonthlyData, fetchWeeklyOccupancyData]);

    useEffect(() => {
        // console.log("The selected week in the useEffect prior to fetching daily lpr occupancy data is::", selectedWeek);
        if (selectedWeek) {
            fetchDailyOccupancyData();
        }
    }, [selectedYear, selectedMonth, selectedWeek, selectedSite?.hwid, weeklyOccupancyData, fetchDailyOccupancyData]);

    return (
        <>
            {/* <div className={'totals-container'}>
                <div className='insights-container'>
                    <span className='insights-label'> Total LPR Occupancy alerts this year</span> <br/>
                    {monthlyLoading ? <BeatLoader color={'white'} size={10} /> :  <span className='insights-value'>{totalAlerts}</span>}
                </div>

            </div> */}
            <div className='graphs-container'>
                <div className='monthly-bar-chart-container'>
                    <MonthlyOccupancyChart
                        selectedYear={selectedYear}
                        monthlyOccupancyData={aggregatedMonthlyData}
                        monthlyDataLoading={monthlyLoading}
                        selectedMonth={selectedMonth}
                        handleMonthlyBarClick={(month) => {
                            setSelectedMonth(month);
                            fetchWeeklyOccupancyData();
                        }}
                        monthlyPercentageIncrease={getPercentageIncrease(aggregatedMonthlyData, selectedMonth, 'month', 'total_count')}
                        monthlyAlerts={getTotalForPeriod(aggregatedMonthlyData, selectedMonth, 'month', 'total_count')}
                        occupancyType={'LPR'}
                    />
                </div>
                <div className='weekly-bar-chart-container'>
                    <WeeklyOccupancyChart
                        selectedYear={selectedYear}
                        weeklyOccupancyData={aggregatedWeeklyData}
                        weeklyDataLoading={weeklyLoading}
                        selectedMonth={selectedMonth}
                        selectedWeek={selectedWeek}
                        relativeWeekNumber={selectedRelativeWeek}
                        handleWeeklyBarClick={(week) => {
                            setSelectedWeek(week);
                            fetchDailyOccupancyData();
                        }}
                        weeklyPercentageIncrease={getPercentageIncrease(aggregatedWeeklyData, selectedWeek, 'week', 'total_count')}
                        weeklyAlerts={getTotalForPeriod(aggregatedWeeklyData, selectedWeek, 'week', 'total_count')}
                        occupancyType={'LPR'}
                    />
                </div>
                <div className='daily-bar-chart-container'>
                    <DailyOccupancyChart
                        selectedYear={selectedYear}
                        dailyOccupancyData={aggregatedDailyData}
                        dailyDataLoading={dailyLoading}
                        selectedMonth={selectedMonth}
                        selectedWeek={selectedWeek}
                        selectedWeekStartDate={selectedWeekStartDate}
                        relativeWeekNumber={selectedRelativeWeek}
                        selectedDate={selectedDate}
                        selectedDayOfWeek={selectedDayOfWeek}
                        dailyPercentageIncrease={getPercentageIncrease(aggregatedDailyData, selectedDate, 'day_number', 'total_count')}
                        dailyAlerts={getTotalForPeriod(aggregatedDailyData, selectedDate, 'day_number', 'total_count')}
                        occupancyType={'LPR'}
                    />
                </div>
            </div>
        </>
    );
}

export default LprOccupancyAnalytics;